import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { fetchApiCalls } from 'api/metrics'
import LoadingWrapper from 'components/LoadingWrapper'
import { useRootLoaderData } from 'components/Root.loader'
import { useState, useEffect, useCallback } from 'react'

import HelpIcon from './HelpIcon'
import UsageGraphComp, { GraphDataType } from './UsageGraphComp'
import UsageThisMonth from './UsageThisMonth'

type Unit = 'day' | 'month'

function padNum(n: number) {
  return n.toString().padStart(2, '0')
}

export default function DashboardComp() {
  const rootData = useRootLoaderData()
  const userGroupConfig = rootData.userGroupConfig
  const [unit, setUnit] = useState<Unit>('day')
  const [ym, setYm] = useState(() => {
    const d = new Date()
    return { y: d.getFullYear(), m: d.getMonth() + 1 } // m: 0 indexed
  })
  const [graphData, setGraphData] = useState<GraphDataType>()

  const lastDay = new Date(ym.y, ym.m, 0).getDate() // m: 0 indexed

  const reloadDay = useCallback(async () => {
    const data = await fetchApiCalls('day', {
      fromDate: `${ym.y}-${padNum(ym.m)}-01`,
      toDate: `${ym.y}-${padNum(ym.m)}-${padNum(lastDay)}`,
    })

    const gDataBase = Array(lastDay)
      .fill(undefined)
      .map((_, i) => ({
        date: `${ym.m}/${i + 1}`,
        count: 0,
      }))

    const graphData: Record<string, any> = {
      invoice: gDataBase.map((i) => ({ ...i })),
      orderForm: gDataBase.map((i) => ({ ...i })),
      template: gDataBase.map((i) => ({ ...i })),
      general: gDataBase.map((i) => ({ ...i })),
    }

    data.forEach(({ apiType, date, apiCallCount: count }) => {
      const dataD = parseInt(date.split('-')[2])
      if (!dataD) return // undefined or 0 or falsy value
      if (!['invoice', 'order_form', 'template', 'general'].includes(apiType)) {
        return
      }
      graphData[apiType === 'order_form' ? 'orderForm' : apiType][dataD - 1].count = count
    })
    if (!userGroupConfig.useInvoiceOCR) graphData.invoice = undefined
    if (!userGroupConfig.useOrderForm) graphData.orderForm = undefined
    if (!userGroupConfig.useOCRBuilder) graphData.template = undefined
    if (userGroupConfig.useConcur) graphData.general = undefined

    setGraphData(graphData)
  }, [ym, lastDay, userGroupConfig])

  const reloadMonth = useCallback(async () => {
    const [startYear, startMonth] = ym.m === 12 ? [ym.y, 1] : [ym.y - 1, ym.m + 1]

    const data = await fetchApiCalls('month', {
      fromDate: `${startYear}-${startMonth}-01`,
      toDate: `${ym.y}-${padNum(ym.m)}-${padNum(lastDay)}`,
    })

    const gDataBase = Array(12)
      .fill(undefined)
      .map((_, i) => {
        const [y, m] =
          startMonth + i > 12 ? [startYear + 1, startMonth - 12 + i] : [startYear, startMonth + i]

        return {
          date: `${y}-${padNum(m)}`,
          count: 0,
        }
      })

    const graphData: Record<string, any> = {
      invoice: gDataBase.map((i) => ({ ...i })),
      orderForm: gDataBase.map((i) => ({ ...i })),
      template: gDataBase.map((i) => ({ ...i })),
      general: gDataBase.map((i) => ({ ...i })),
    }

    data.forEach(({ apiType, date, apiCallCount: count }) => {
      const [_dataY, dataM] = date.split('-').map((part) => parseInt(part))
      // need to assert year of dataY?
      if (!dataM) return // undefined
      const i = dataM >= startMonth ? dataM - startMonth : 12 - (startMonth - dataM)
      if (['invoice', 'order_form', 'template', 'general'].includes(apiType)) {
        graphData[apiType === 'order_form' ? 'orderForm' : apiType][i].count = count
      } else {
        // should not be here
      }
    })

    if (!userGroupConfig.useInvoiceOCR) graphData.invoice = undefined
    if (!userGroupConfig.useOrderForm) graphData.orderForm = undefined
    if (!userGroupConfig.useOCRBuilder) graphData.template = undefined
    if (userGroupConfig.useConcur) graphData.general = undefined

    setGraphData(graphData)
  }, [ym, lastDay, userGroupConfig])

  useEffect(() => {
    unit === 'day' ? reloadDay() : reloadMonth()
  }, [unit, reloadDay, reloadMonth])

  const handleUnitSelect = (u: Unit) => {
    setUnit(u)
  }
  const handleRangeChange = (y: number, m: number) => {
    setYm({ y, m })
  }

  return (
    <LoadingWrapper loaded={!!graphData}>
      <Stack direction="row" alignItems="center">
        <h2>ダッシュボード</h2>
        <HelpIcon />
      </Stack>
      <UsageThisMonth />
      <Box sx={{ pb: 4 }}>
        <UsageGraphComp
          year={ym.y}
          month={ym.m}
          unit={unit}
          data={graphData}
          handleUnitSelect={handleUnitSelect}
          onChangeRange={handleRangeChange}
        />
      </Box>
    </LoadingWrapper>
  )
}
